/** Prefilled message when contacting a service via WhatsApp or Viber. */
export const SERVICE_MESSENGER_PREFILL =
  'Pozdrav! Javljam se preko platforme proslava.ba.'

/** Digits only; optional leading + for E.164-style tel:/wa.me links. */
export function normalizePhoneDigits(raw: string): string {
  let digits = raw.replace(/\D/g, '')
  if (digits.startsWith('00')) digits = digits.slice(2)
  if (digits.startsWith('0')) digits = `387${digits.slice(1)}`
  if (digits.length > 0 && digits.length <= 9 && !digits.startsWith('387')) {
    digits = `387${digits}`
  }
  return digits
}

export function phoneToTelHref(raw: string): string | null {
  const digits = normalizePhoneDigits(raw)
  if (!digits) return null
  return `tel:+${digits}`
}

export function emailToMailtoHref(raw: string): string | null {
  const email = raw.trim()
  if (!email || !email.includes('@')) return null
  return `mailto:${email}`
}

export function whatsappChatHref(
  raw: string,
  options?: { prefill?: string | false },
): string | null {
  const digits = normalizePhoneDigits(raw)
  if (!digits) return null
  const prefill =
    options?.prefill === false ? null : (options?.prefill ?? SERVICE_MESSENGER_PREFILL)
  if (!prefill) return `https://wa.me/${digits}`
  return `https://wa.me/${digits}?text=${encodeURIComponent(prefill)}`
}

export function viberChatHref(
  raw: string,
  options?: { prefill?: string | false },
): string | null {
  const digits = normalizePhoneDigits(raw)
  if (!digits) return null
  const base = `viber://chat?number=${encodeURIComponent(`+${digits}`)}`
  const prefill =
    options?.prefill === false ? null : (options?.prefill ?? SERVICE_MESSENGER_PREFILL)
  if (!prefill) return base
  return `${base}&draft=${encodeURIComponent(prefill)}`
}

/** Opens Viber chat; copies prefill to clipboard when the OS cannot inject draft text. */
export async function openViberChat(raw: string): Promise<void> {
  const href = viberChatHref(raw)
  if (!href || typeof window === 'undefined') return
  try {
    await navigator.clipboard.writeText(SERVICE_MESSENGER_PREFILL)
  } catch {
    /* clipboard optional */
  }
  window.location.href = href
}

export function isAllowedChatLinkHref(href: string): boolean {
  const h = href.trim()
  return /^(https?:\/\/|\/|tel:|mailto:|viber:)/i.test(h)
}

export function isChatActionLink(href: string): boolean {
  return /^(tel:|mailto:|viber:)/i.test(href.trim()) || /^https:\/\/wa\.me\//i.test(href.trim())
}

/** Facebook, Instagram i web — otvaraju se u novom tabu. */
export function isChatBlankTargetLink(href: string, label?: string): boolean {
  const lab = (label ?? '').trim().toLowerCase()
  if (lab === 'web stranica' || lab === 'instagram' || lab === 'facebook') {
    return true
  }
  const h = href.trim().toLowerCase()
  return /facebook\.com|fb\.com|instagram\.com|instagr\.am/i.test(h)
}

const PLACEHOLDER_WEB = /^(n\/a|nema|none|—|-|\.)$/i

/** True only when the service has a real website URL in CMS (not empty / placeholder). */
export function hasServiceWebsiteUrl(url: string | null | undefined): boolean {
  const raw = url?.trim()
  if (!raw || PLACEHOLDER_WEB.test(raw)) return false
  const lower = raw.toLowerCase()
  if (/^https?:\/\/?$/i.test(lower)) return false
  if (/^https?:\/\//i.test(lower)) return true
  return raw.includes('.') && !PLACEHOLDER_WEB.test(raw)
}
